import React, { createContext, useContext, useState, useEffect } from 'react';
import { useAuth } from './AuthContext';
import { fetchClinicBranding } from './clinicApi';

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within a BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const { userProfile, getAccessToken } = useAuth();
  const [branding, setBranding] = useState({
    logo: null,
    logoPreview: null,
    primaryColor: "#3e16d0",
    secondaryColor: "#1585cb",
    customHeaderText: "",
    showLogoInHeader: true,
  });
  const [loading, setLoading] = useState(true);

  // Load branding from backend API and localStorage
  useEffect(() => {
    const loadBranding = async () => {
     
      
      if (userProfile?.branch_id && userProfile?.tenant_id) {
        try {
          // First try to load from localStorage for immediate display (use tenant_id for shared branding)
          const savedBranding = localStorage.getItem(`branding_${userProfile.tenant_id}`);
         
          
          if (savedBranding) {
            try {
              const parsedBranding = JSON.parse(savedBranding);
              
              setBranding(prev => ({ ...prev, ...parsedBranding }));
            } catch (error) {
              console.error('Error parsing saved branding:', error);
            }
          }

          // Then load from backend API to ensure we have the latest data
          const token = getAccessToken();
          
          
          if (token) {
           
            // Use branch_id to fetch branding from the specific branch
            const response = await fetchClinicBranding(userProfile.branch_id, token);
           
            if (response.ok) {
              const apiBranding = await response.json();
              
              
              const updatedBranding = {
                logo: apiBranding.logo || null,
                logoPreview: apiBranding.logo_preview || null,
                primaryColor: apiBranding.primary_color || "#3e16d0",
                secondaryColor: apiBranding.secondary_color || "#1585cb",
                customHeaderText: apiBranding.custom_header_text || "",
                showLogoInHeader: apiBranding.show_logo_in_header !== false,
              };
              
             
              setBranding(updatedBranding);
              
              // Save to localStorage for future use (use tenant_id for shared branding)
              localStorage.setItem(`branding_${userProfile.tenant_id}`, JSON.stringify(updatedBranding));
              
            } else {
              
              const errorText = await response.text();
             
            }
          } else {
            console.log('BrandingContext: No token available, skipping API call');
          }
        } catch (error) {
          console.error('Error loading branding from API:', error);
        } finally {
          setLoading(false);
        }
      } else {
        console.log('BrandingContext: No userProfile.branch_id or tenant_id available');
        setLoading(false);
      }
    };

    loadBranding();
  }, [userProfile?.branch_id, userProfile?.tenant_id, getAccessToken]);

  const updateBranding = async (newBranding) => {
    setBranding(prev => {
      const updated = { ...prev, ...newBranding };
      
      // Save to localStorage using tenant_id for shared branding
      if (userProfile?.tenant_id) {
        localStorage.setItem(`branding_${userProfile.tenant_id}`, JSON.stringify(updated));
      }
      
      return updated;
    });
  };

  const resetBranding = () => {
    const defaultBranding = {
      logo: null,
      logoPreview: null,
      primaryColor: "#3e16d0",
      secondaryColor: "#1585cb",
      customHeaderText: "",
      showLogoInHeader: true,
    };
    
    setBranding(defaultBranding);
    
    if (userProfile?.clinic_id) {
      localStorage.removeItem(`branding_${userProfile.clinic_id}`);
    }
  };

  const value = {
    branding,
    updateBranding,
    resetBranding,
    loading,
  };

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
}; 